/**
 * Derivações das disputas de governador do 2º turno (§11.1.1; FE §6.9): margem, tom do selo e do mapa, contagem do
 * painel, ordens da grade e da lista, tamanho de página da lista e o ponto "em apuração" da aba (§6.3) e do herói (§7.9).
 * Puro (sem Preact nem sinais). As UFs em disputa são as chaves de `CandidateList.governor` (nunca uma lista fixa no
 * código); todo par segue a ordem do contrato [A, B] = [número menor, número maior] e nada aqui reordena candidatos por
 * votos (neutralidade, §1.3): só a ORDEM DAS UFs muda com os dados, por regras simétricas (margem absoluta, fase,
 * eleitorado e nome).
 */
import { placeName } from '../format/places';
import { racePhaseOf } from './racePhase';
import type { Candidate, Decision, LayoutMode, PlaceView, RacePhase, ResultsView, UfCode } from './types';

/** `ResultsView.governor[UF]`. */
export type GovView = PlaceView & { candidates: [Candidate, Candidate] };

/** Tom do selo, da linha e do mapa (§11.1.1, §9.4.9). */
export type RaceTone = 'm0' | 'm1' | 'm3' | 'nodata' | 'norace';

/** Fases que uma UF em disputa pode ter no 2º turno (sem `no_race` nem `runoff`). */
export type OpenRacePhase = Extract<RacePhase, 'no_data' | 'counting' | 'decided'>;

/** Abaixo desta margem (fração dos válidos) a disputa apurando usa o degrau `m0`; a partir dela, `m1` (§3.3.5). */
export const RACE_M0_BELOW = 0.1;
/** UFs do país; as que não têm 2º turno definiram o governador no 1º turno (§11.1, linha do 1º turno). */
export const UF_TOTAL = 27;
/** Altura fixa da `RaceRow` (§11.1 `RaceList` bloco 5). */
export const RACE_ROW_PX = 78;
/** Altura disponível abaixo da qual o título da `RaceList` some (§11.1 `RaceList` bloco 4). */
export const RACE_TITLE_MIN_PX = 70;
/** Linhas por página no `tablet` e no `land` (§11.1 `GovernorsPanel` bloco 6). */
export const RACE_PHONE_PAGE = 2;

/** Diferença entre as fatias dos válidos dos dois candidatos (fração; simétrica). */
export const raceMargin = (g: Pick<PlaceView, 'shares'>): number => Math.abs(g.shares[0] - g.shares[1]);

/** §11.1.1: tom pela fase; apurando, no máximo `m1` (§3.3.5). */
export function raceTone(g: Pick<PlaceView, 'shares'>, phase: RacePhase): RaceTone {
  if (phase === 'no_race') return 'norace';
  if (phase === 'no_data') return 'nodata';
  if (phase === 'decided') return 'm3';
  return raceMargin(g) < RACE_M0_BELOW ? 'm0' : 'm1';
}

/** §11.1.1: alguma disputa de governador ainda não definida (ponto "em apuração" da aba, §6.3). */
export function governorsCounting(v: ResultsView): boolean {
  return Object.values(v.governor).some((g) => g !== undefined && racePhaseOf(g) !== 'decided');
}

/**
 * UFs da linha "Ainda em apuração" do herói (§7.9 bloco 3, dona): `status` 'counting' ou 'no_data', sem `mathDecision`
 * nem `tseResult`, em ordem alfabética de sigla. Uma disputa completa sem decisão publicada (PONTO DE EXTENSÃO de
 * §11.4) não entra aqui, mas continua "em apuração" para a aba (`governorsCounting`, §6.3): cada seção dona manda na
 * sua regra.
 */
export function stillCountingUfs(governor: ResultsView['governor']): UfCode[] {
  const out: UfCode[] = [];
  for (const [uf, g] of Object.entries(governor) as Array<[UfCode, GovView | undefined]>) {
    if (!g || g.mathDecision || g.tseResult) continue;
    if (g.status === 'counting' || g.status === 'no_data') out.push(uf);
  }
  return out.sort();
}

/** UFs com 2º turno (§11.1.1): as chaves do cadastro; sem ele, as da vista (que nascem das mesmas chaves). */
export function raceUfs(
  list: Partial<Record<UfCode, unknown>> | null | undefined,
  view: Pick<ResultsView, 'governor'> | null,
): UfCode[] {
  const src = list ?? view?.governor ?? {};
  return (Object.keys(src) as UfCode[]).filter((uf) => src[uf] !== undefined && src[uf] !== null).sort();
}

/** Uma disputa pronta para a UI (selo, linha, contagem). */
export interface RaceModel {
  uf: UfCode;
  phase: OpenRacePhase;
  /** Tom do selo e da linha (`nodata`, `m0`, `m1`, `m3`). */
  tone: Exclude<RaceTone, 'norace'>;
  /** Ordem de número (A, B). */
  candidates: readonly [Candidate, Candidate];
  /** Fatias dos válidos [A, B]; [0, 0] sem votos. */
  shares: readonly [number, number];
  sectionsFraction: number;
  margin: number;
  /** Lado da cor: vencedor (definida) ou líder (apurando); null sem votos ou com empate exato. */
  side: 0 | 1 | null;
  /** Decisão exibida: a do TSE quando houver, senão a matemática (§8.1, F44). */
  decision: Decision | null;
  /** Eleitorado de referência (`PlaceInfo.electorate_ref`); 0 sem a geografia. */
  electorate: number;
}

const ZERO: readonly [number, number] = Object.freeze([0, 0]) as readonly [number, number];

/**
 * Modelo da disputa da UF: da vista (`ResultsView.governor[uf]`) ou, sem ela (carregando, antes das 17h00), do cadastro
 * (nomes, "sem dados"). null quando a UF não tem nem vista nem cadastro.
 */
export function raceModel(
  uf: UfCode,
  g: GovView | undefined,
  list: readonly [Candidate, Candidate] | undefined,
  electorate: number,
): RaceModel | null {
  const candidates = g?.candidates ?? list;
  if (!candidates) return null;
  if (!g) {
    return {
      uf,
      phase: 'no_data',
      tone: 'nodata',
      candidates,
      shares: ZERO,
      sectionsFraction: 0,
      margin: 0,
      side: null,
      decision: null,
      electorate,
    };
  }
  const phase = racePhaseOf(g) as OpenRacePhase;
  const decision = g.tseResult ?? g.mathDecision;
  let side: 0 | 1 | null = null;
  if (phase === 'decided' && decision) {
    side = candidates[0].number === decision.winner ? 0 : candidates[1].number === decision.winner ? 1 : g.leader;
  } else if (phase === 'counting') side = g.leader;
  return {
    uf,
    phase,
    tone: raceTone(g, phase) as RaceModel['tone'],
    candidates,
    shares: phase === 'no_data' ? ZERO : g.shares,
    sectionsFraction: g.sectionsFraction,
    margin: phase === 'no_data' ? 0 : raceMargin(g),
    side,
    decision: phase === 'decided' ? decision : null,
    electorate,
  };
}

/** Entradas de `raceModels`. */
export interface RaceModelsInput {
  /** `CandidateList.governor` (null antes do cadastro). */
  list: Partial<Record<UfCode, [Candidate, Candidate]>> | null | undefined;
  /** Vista exibida; null carregando ou antes das 17h00 (E01: nenhum número antes da abertura). */
  view: Pick<ResultsView, 'governor'> | null;
  /** Eleitorado de referência por UF (`Geography.places`); 0 quando ainda não chegou. */
  electorateOf: (uf: UfCode) => number;
}

/** Modelos de todas as disputas, em ordem alfabética de sigla (as ordens de exibição vêm depois). */
export function raceModels({ list, view, electorateOf }: RaceModelsInput): RaceModel[] {
  const out: RaceModel[] = [];
  for (const uf of raceUfs(list, view)) {
    const m = raceModel(uf, view?.governor[uf] as GovView | undefined, list?.[uf], electorateOf(uf));
    if (m) out.push(m);
  }
  return out;
}

/** Desempate comum (§11.1 `RaceList` item 6): eleitorado decrescente, depois o nome da UF (pt-BR). */
function tieBreak(a: RaceModel, b: RaceModel): number {
  return b.electorate - a.electorate || placeName(a.uf).localeCompare(placeName(b.uf), 'pt-BR');
}

/** Ordem da `UfStrip` (§11.1 bloco 4): definidas → em apuração/sem dados; no grupo, eleitorado decrescente. */
export function stripOrder(races: readonly RaceModel[]): RaceModel[] {
  const group = (r: RaceModel) => (r.phase === 'decided' ? 0 : 1);
  return [...races].sort((a, b) => group(a) - group(b) || tieBreak(a, b));
}

/**
 * Ordem da `RaceList` "das mais apertadas" (§11.1 bloco 3): abertas antes das definidas; entre as abertas, com votos
 * antes de sem votos; abertas e definidas por margem crescente; empates por eleitorado e nome.
 */
export function listOrder(races: readonly RaceModel[]): RaceModel[] {
  const rank = (r: RaceModel) => (r.phase === 'counting' ? 0 : r.phase === 'no_data' ? 1 : 2);
  return [...races].sort((a, b) => rank(a) - rank(b) || a.margin - b.margin || tieBreak(a, b));
}

/** Contagem do painel (§11.1 bloco 2). */
export interface GovernorsTally {
  /** Fase `decided`. */
  decided: number;
  /** Fases `counting` e `no_data` (sem votos conta como "em apuração"). */
  counting: number;
  /** Disputas do 2º turno. */
  total: number;
  /** UFs sem 2º turno: definiram o governador no 1º turno. */
  firstRound: number;
}

export function governorsTally(races: readonly RaceModel[]): GovernorsTally {
  const decided = races.filter((r) => r.phase === 'decided').length;
  return {
    decided,
    counting: races.length - decided,
    total: races.length,
    firstRound: Math.max(0, UF_TOTAL - races.length),
  };
}

/**
 * Linhas por página da `RaceList` (§11.1 bloco 5 e 6): `flow` mostra todas (sem `Pager`); `tablet` e `land`, 2; desktop
 * e TV, `floor(altura disponível / 78)`, no mínimo 1. `bodyPx` null = ainda não medido (todas, até a 1ª medida).
 */
export function racePageSize(mode: LayoutMode, bodyPx: number | null, n: number): number {
  if (mode === 'flow') return Math.max(1, n);
  if (mode === 'tablet' || mode === 'land') return RACE_PHONE_PAGE;
  if (bodyPx === null) return Math.max(1, n);
  return Math.max(1, Math.floor(bodyPx / RACE_ROW_PX));
}
